/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2023-2024 (original work) Open Assessment Technologies SA ;
 */

import config from '@/config';
import { getPermissionsGuard } from '@/core/guards/permissionsGuard';
import { multiGuard } from '@/core/guards/guardComposer';

export const routing = [
    {
        path: [config.routes.sessions.list],
        guard: getPermissionsGuard([
            { resource: 'portal.session', scopes: ['view'] },
            { resource: 'portal.session', scopes: ['list'] },
            { resource: 'portal.public-session', scopes: ['view'] }
        ]),
        controller: () => import('./controllers/sessions')
    },
    {
        path: [config.routes.sessions.monitoring],
        guard: getPermissionsGuard([
            { resource: 'portal.session', scopes: ['view'] },
            { resource: 'portal.public-session', scopes: ['view'] }
        ]),
        controller: () => import('./controllers/sessionMonitoring')
    },
    {
        path: [config.routes.sessions.overview],
        guard: getPermissionsGuard([
            { resource: 'portal.session', scopes: ['view'] },
            { resource: 'portal.public-session', scopes: ['view'] },
            { resource: 'portal.paperbased-session', scopes: ['view'] }
        ]),
        controller: () => import('./controllers/sessionOverview')
    },
    {
        path: [config.routes.sessions.sessionDetails],
        guard: getPermissionsGuard([
            { resource: 'portal.group', scopes: ['view'] },
            { resource: 'portal.session', scopes: ['view'] },
            { resource: 'portal.review-session', scopes: ['view', 'edit'] },
            { resource: 'portal.organization', scopes: ['view', 'edit'] },
            { resource: 'portal.public-session', scopes: ['view'] }
        ]),
        controller: () => import('./controllers/sessionDetails')
    },
    {
        path: [config.routes.sessions.sessionEnrollCandidate],
        // the page enrols (portal.session:enrol) and searches candidates (portal.candidate:view);
        // two guards because a single one accepts either permission
        guard: multiGuard([
            getPermissionsGuard([{ resource: 'portal.session', scopes: ['enrol'] }]),
            getPermissionsGuard([{ resource: 'portal.candidate', scopes: ['view'] }])
        ]),
        controller: () => import('./controllers/sessionEnrollCandidate')
    },
    {
        path: [config.routes.sessions.sessionRoomManagement],
        guard: getPermissionsGuard([{ resource: 'portal.session-room', scopes: ['create'] }])
    },
    {
        path: [config.routes.sessions.sessionMoveCandidate],
        // the move endpoints are guarded by portal.session:enrol on the API
        guard: getPermissionsGuard([{ resource: 'portal.session', scopes: ['enrol'] }]),
        controller: () => import('./controllers/moveCandidate')
    },
    {
        path: [config.routes.sessions.edit],
        guard: getPermissionsGuard([
            { resource: 'portal.session', scopes: ['edit'] },
            { resource: 'portal.public-session', scopes: ['edit'] }
        ]),
        controller: () => import('./controllers/session')
    },
    {
        path: [config.routes.sessions.create],
        guard: getPermissionsGuard([
            { resource: 'portal.session', scopes: ['create'] },
            { resource: 'portal.public-session', scopes: ['create'] }
        ]),
        controller: () => import('./controllers/session')
    },
    {
        path: [config.routes.sessions.reports],
        controller: () => import('./controllers/sessionReports'),
        guard: getPermissionsGuard([{ resource: 'portal.session', scopes: ['edit'] }])
    },
    {
        path: [config.routes.sessions.certificationSessionsList],
        guard: getPermissionsGuard([{ resource: 'portal.session', scopes: ['view'] }]),
        controller: () => import('./controllers/certificationSessions')
    },
    {
        path: [config.routes.sessions.editSessionCluster],
        guard: getPermissionsGuard([
            { resource: 'portal.session-cluster', scopes: ['edit'] },
            { resource: 'portal.session-cluster', scopes: ['create'] },
            { resource: 'portal.campaign', scopes: ['view'] },
            { resource: 'portal.session-template', scopes: ['view'] }
        ]),
        controller: () => import('./controllers/sessionCluster')
    },
    {
        path: [config.routes.sessions.sessionClusterDetails],
        guard: getPermissionsGuard([
            { resource: 'portal.group', scopes: ['view'] },
            { resource: 'portal.session', scopes: ['view'] },
            { resource: 'portal.review-session', scopes: ['view', 'edit'] },
            { resource: 'portal.organization', scopes: ['view', 'edit'] },
            { resource: 'portal.public-session', scopes: ['view'] },
            { resource: 'portal.session-cluster', scopes: ['edit'] },
            { resource: 'portal.session-cluster', scopes: ['create'] }
        ]),
        controller: () => import('./controllers/sessionDetails')
    },
    {
        path: [config.routes.sessions.sessionClusterEditSession],
        guard: getPermissionsGuard([
            { resource: 'portal.group', scopes: ['view'] },
            { resource: 'portal.session', scopes: ['view'] },
            { resource: 'portal.review-session', scopes: ['view', 'edit'] },
            { resource: 'portal.organization', scopes: ['view', 'edit'] },
            { resource: 'portal.public-session', scopes: ['view'] },
            { resource: 'portal.session-cluster', scopes: ['edit'] },
            { resource: 'portal.session-cluster', scopes: ['create'] }
        ]),
        controller: () => import('./controllers/session')
    }
];
